import React, { ReactNode, useEffect, useState } from "react";
import { Resource, Scope } from "../../../../../../types/NendaTypes";
import PermissionsGate from "../../../components/PermissionGate";
import { t } from "i18next";
import { BasicTable } from "../../../../Table/BasicTable";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffectNotOnMount } from "../../../../../hooks/useEffectNotOnMount";
import { FaVideo } from "react-icons/fa";
import { Form, Formik } from "formik";
import { Modal } from "../../../../Modal/Modal";
import { FormField } from "../../../../Form/FormField";
import { store } from "../../../store";
import {
  CreateChannelAction,
  GetChannelsAction,
  getLiveChannelSelector,
  getLiveChannelsSelector,
  getPaginatorSelector,
} from "../../../store/reducers/cmsReducer";
import CMSPageHeader from "../../../ui-components/pageLayout/CMSPageHeader";
import { ModalWrapper } from "../../../ui-components/pageLayout/ModalComponents";
import { LiveChannelsTableSettings } from "./LiveChannelsTableSettings";

interface CreateChannelValues {
  name: string;
}

const CreateChannelSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required*"),
});

const LiveChannels: React.FC = (): ReactNode => {
  const [showForm, setShowForm] = React.useState(false);
  const channels = useSelector(getLiveChannelsSelector);
  const channel = useSelector(getLiveChannelSelector);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const pagination = useSelector(getPaginatorSelector);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [currentRowsPerPage, setCurrentRowsPerPage] = useState(25);
  const history = useNavigate();

  const fetchChannels = (query: string = "") => {
    store.dispatch(GetChannelsAction(query));
  };

  useEffect(() => {
    fetchChannels("page=1&pageSize=25");
  }, []);

  function submitCreateChannel(values) {
    setIsCreating(true);
    store.dispatch(CreateChannelAction(values.name));
  }

  const handleRowClick = (id: string) => {
    redirect(id);
  };

  const buildQuery = (page: number, pageSize: number, search: string) => {
    let query = `page=${page}&pageSize=${pageSize}`;
    if (search) query += `&originalTitle=${search}`;
    return query;
  };

  const handlePagination = (page: number) => {
    setCurrentPage(page);
    fetchChannels(buildQuery(page, currentRowsPerPage, search));
  };

  const handleRowsPerPage = (rowsPerPage: number) => {
    setCurrentPage(1);
    setCurrentRowsPerPage(rowsPerPage);
    fetchChannels(buildQuery(1, rowsPerPage, search));
  };

  const handleSearch = (search: string) => {
    setSearch(search);
    setCurrentPage(1);
    fetchChannels(buildQuery(1, currentRowsPerPage, search));
  };

  useEffectNotOnMount(() => {
    if (channel && isCreating) {
      redirect(channel.getUniqueId());
    }
  }, [channel]);

  const redirect = (id) => {
    history(`/admin/metadata/live-channels/${id}`);
  };

  return (
    <PermissionsGate
      restriction={{
        resource: Resource.Content,
        scopes: [Scope.CanAdministrate],
      }}
    >
      <>
        <CMSPageHeader
          title={t("customerportal.pages.live_channels.title")}
          count={pagination.total}
          button={{
            title: t("customerportal.pages.live_channels.create"),
            onClick: () => setShowForm(true),
            icon: FaVideo,
          }}
          onSearch={handleSearch}
        />
        <Formik
          initialValues={{
            name: "",
          }}
          validationSchema={CreateChannelSchema}
          onSubmit={(values: CreateChannelValues) => {
            submitCreateChannel(values);
            setShowForm(false);
          }}
        >
          {({ handleSubmit }) => (
            <Form>
              <ModalWrapper>
                <Modal
                  style={{}}
                  visible={showForm}
                  onClose={() => setShowForm(false)}
                  title={"Create new channel"}
                  confirmText="Create"
                  onSubmit={handleSubmit}
                  closeOnSubmit={false}
                >
                  <FormField
                    name="name"
                    label="name"
                    placeholder="Channel Name..."
                  />
                </Modal>
              </ModalWrapper>
            </Form>
          )}
        </Formik>
        <BasicTable
          tableSettings={LiveChannelsTableSettings}
          tableData={channels || []}
          onRowClick={handleRowClick}
          paginationControls={{
            paginationCount: pagination.total,
            handlePagination: handlePagination,
            handleRowsPerPage: handleRowsPerPage,
            currentPage: currentPage,
            currentRowsPerPage: currentRowsPerPage,
          }}
        />
      </>
    </PermissionsGate>
  );
};

export default LiveChannels;
